/**
 * Minimal data-fetching hook. No external query library: a single in-flight
 * request per key, abortable, with loading/error/data + a manual refresh. The
 * typed ApiError surfaces the 403 "not on the teacher allowlist" case so pages
 * can show an access notice rather than a generic failure.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

interface ApiResult<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  refresh: () => void;
  setData: (next: T) => void;
}

export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): ApiResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(undefined);
    fetcherRef
      .current(controller.signal)
      .then((res) => {
        if (active) setData(res);
      })
      .catch((e: unknown) => {
        if (!active || controller.signal.aborted) return;
        setError(
          e instanceof ApiError ? e : new ApiError(0, { error: 'Network error', code: 'network' }),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [nonce, ...deps]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, refresh, setData };
}
